"use client";

import React, { useContext } from "react";
import { Input as AntInput, DatePicker, TimePicker } from "antd";
import dayjs from "dayjs";
import { twMerge } from "tailwind-merge";
import { CMSModal } from "@/context";

type InputProps = {
  label?: string;
  placeholder?: string;
  type?: "text" | "date" | "time" | "email" | "password"|"number";
  name?: string;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  className?: string;
  required?: boolean;
  icon?: React.ReactNode;
  readonly?: boolean;
  maxLength?: number;
};

const Input: React.FC<InputProps> = ({
  label = "",
  placeholder = "",
  type = "text",
  name,
  value,
  onChange,
  onBlur,
  disabled = false,
  className = "",
  required = false,
  icon,
  readonly = false,
  maxLength,
}) => {
  const { theme } = useContext(CMSModal);

  const commonClass = twMerge("w-full h-[40px]", className);

  const renderInput = () => {
    if (type === "date") {
      return (
        <DatePicker
          value={value ? dayjs(value) : null}
          onChange={(date) => onChange?.(date?.format("YYYY-MM-DD") || "")}
          className={commonClass}
          disabled={disabled}
        />
      );
    }

    if (type === "time") {
      return (
        <TimePicker
          value={value ? dayjs(value, "HH:mm") : null}
          format="HH:mm"
          onChange={(time) => onChange?.(time?.format("HH:mm") || "")}
          className={commonClass}
          disabled={disabled}
        />
      );
    }
    if (type === "password") {
      return (
        <AntInput.Password
          name={name}
          placeholder={placeholder}
          value={value}
          disabled={disabled}
          maxLength={maxLength}
          readOnly={readonly}
          prefix={icon}
          onChange={(e) => onChange?.(e.target.value)}
          className={commonClass}
        />
      );
    }
    if (type === "email") {
      const isInvalid = value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

      return (
        <AntInput
          type="email"
          name={name}
          placeholder={placeholder}
          value={value}
          disabled={disabled}
          maxLength={maxLength}
          readOnly={readonly}
          prefix={icon}
          status={isInvalid ? "error" : ""}
          autoComplete="email"
          onChange={(e) => onChange?.(e.target.value)}
          className={commonClass}
        />
      );
    }

    return (
      <AntInput
        name={name}
        placeholder={placeholder}
        value={value}
        disabled={disabled || readonly}
        maxLength={maxLength}
        readOnly={readonly}
        prefix={icon}
        onChange={(e) => onChange?.(e.target.value)}
        onBlur={onBlur}
        className={commonClass}
      />
    );
  };

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label
          htmlFor={name}
          className={twMerge(
            "text-sm",
            theme === "dark" ? "text-gray-300" : "text-gray-700",
          )}
        >
          {required && <span className="text-red-500">*</span>} {label}
        </label>
      )}

      {renderInput()}
    </div>
  );
};

export default Input;
